import React, { useMemo, useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import type { DndOrderedListReorder } from '~/components/core/DndOrderedList/DndOrderedList.types';
import EmptyState from '~/components/core/EmptyState';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { Text } from '~/components/core/TextComponents';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { reportAxiosError, subOrgIdToNameDict } from '~/Utils';

import GlobalFieldDialog from './GlobalFieldDialog';
import GlobalFieldRow from './GlobalFieldRow';
import GlobalFieldsTableHeader from './GlobalFieldsTableHeader';
import type { GlobalFieldAnchor, GlobalFieldPlacement, GlobalFieldRow as GlobalFieldRowType } from './types';
import { ANCHOR_LABELS, GLOBAL_FIELD_ANCHOR, globalFieldsUrl, PLACEMENT_LABELS } from './types';

/** Why dragging goes inert under a search: the reorder endpoint takes the whole target order of a
 *  bucket and rejects anything that is not an exact permutation of it, so a drag inside a filtered
 *  subset has no valid body — and order only means anything across the full bucket anyway. */
const REORDER_DISABLED_NOTE = 'Clear the search to change field order.';

interface GlobalFieldsTabProps {
  /** The placements this tab owns. A single-entry list hides the Placement column. */
  placements: GlobalFieldPlacement[];
  description: string;
}

const ANCHORS: GlobalFieldAnchor[] = [GLOBAL_FIELD_ANCHOR.Start, GLOBAL_FIELD_ANCHOR.End];

const GlobalFieldsTab: React.FC<GlobalFieldsTabProps> = ({ placements, description }) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore useSysconfig is an untyped JSX module.
  const { organization } = useSysconfig();
  const { lobConfigurationsDict } = useLobConfiguration();
  const baseUrl = globalFieldsUrl(organization.id);

  const { isLoading, isError, data: allFields, reloadData } = useDataFetcher<GlobalFieldRowType[]>(baseUrl);

  const [searchText, setSearchText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [dialogState, setDialogState] = useState<{ open: boolean; field: GlobalFieldRowType | null }>({
    open: false,
    field: null,
  });

  // Identical for every row — build once per `organization`, not per cell render.
  const subOrgIdToName = useMemo(() => subOrgIdToNameDict(organization), [organization]);
  const showPlacementColumn = placements.length > 1;

  const tabFields = useMemo(
    () => (allFields || []).filter((field) => placements.includes(field.placement)),
    [allFields, placements]
  );
  // One trimmed needle decides both whether the list is filtered and what it is filtered by. Were
  // they to disagree, a whitespace-only search would narrow the list while leaving drag live, and
  // a drag's indices into the narrowed list would be applied to the whole bucket.
  const needle = searchText.trim().toLowerCase();
  const isFiltering = needle.length > 0;
  const matchesSearch = (field: GlobalFieldRowType) => {
    return (
      field.key.toLowerCase().includes(needle) ||
      String(field.definition?.desc ?? '')
        .toLowerCase()
        .includes(needle)
    );
  };

  const runWrite = async (write: () => Promise<unknown>) => {
    setIsProcessing(true);
    try {
      await write();
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEnabledChanged = (field: GlobalFieldRowType) =>
    runWrite(() => axios.patch(`${baseUrl}/${field.id}`, { is_enabled: !field.is_enabled }));

  const handleDelete = (field: GlobalFieldRowType) => runWrite(() => axios.delete(`${baseUrl}/${field.id}`));

  const handleReorder = (
    placement: GlobalFieldPlacement,
    anchor: GlobalFieldAnchor,
    bucket: GlobalFieldRowType[],
    { fromIndex, toIndex }: DndOrderedListReorder
  ) => {
    const ordered = [...bucket];
    const [moved] = ordered.splice(fromIndex, 1);
    ordered.splice(toIndex, 0, moved);
    return runWrite(() =>
      axios.post(`${baseUrl}/reorder`, {
        placement,
        anchor,
        // The whole bucket, not just the moved row: the endpoint rejects anything that is not an
        // exact permutation, which is what stops a filtered or stale list from being applied.
        ordered_ids: ordered.map((field) => field.id),
      })
    );
  };

  // One section per (placement, anchor) that holds fields. That pairing is the unit the backend
  // orders within, so it is also the unit a drag can move a row inside.
  const buckets = placements.flatMap((placement) =>
    ANCHORS.map((anchor) => ({
      placement,
      anchor,
      fields: tabFields
        .filter((field) => field.placement === placement && field.anchor === anchor)
        .sort((left, right) => left.position - right.position || left.id - right.id),
    })).filter((bucket) => bucket.fields.length > 0)
  );

  const visibleBuckets = buckets
    .map((bucket) => ({ ...bucket, visibleFields: bucket.fields.filter(matchesSearch) }))
    .filter((bucket) => bucket.visibleFields.length > 0);
  const visibleCount = visibleBuckets.reduce((total, bucket) => total + bucket.visibleFields.length, 0);

  return (
    <>
      <CardDialog noCardTitle>
        <Text variant="small" weight="regular">
          {description}
        </Text>
        <div className="flex flex-col gap-16">
          <div className="my-4 w-1/4">
            <TextField
              fullWidth
              value={searchText}
              onChange={(value: string) => setSearchText(value)}
              placeholder="Search Fields"
            />
          </div>
          <div className="flex justify-between">
            <Text weight={Text.WEIGHTS.SEMI_BOLD} variant={Text.VARIANTS.SM}>
              {`${visibleCount} ${visibleCount === 1 ? 'Field' : 'Fields'}`}
            </Text>
            <Button color="primary" onClick={() => setDialogState({ open: true, field: null })}>
              + Add Field
            </Button>
          </div>
        </div>
        <LoadingSwitch isLoading={isLoading} isError={isError}>
          {visibleBuckets.length === 0 ? (
            <EmptyState
              subtitle={isFiltering ? 'No fields match your search.' : 'No global fields yet. Add one to get started.'}
            />
          ) : (
            <div className="flex flex-col gap-24">
              {visibleBuckets.map(({ placement, anchor, fields, visibleFields }) => (
                <div key={`${placement}-${anchor}`} className="flex flex-col gap-8">
                  <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                    {showPlacementColumn
                      ? `${PLACEMENT_LABELS[placement]} — ${ANCHOR_LABELS[anchor]}`
                      : ANCHOR_LABELS[anchor]}
                  </Text>
                  <GlobalFieldsTableHeader showPlacementColumn={showPlacementColumn} />
                  <DndOrderedList
                    items={visibleFields}
                    getItemId={(field) => String(field.id)}
                    disabled={isFiltering || isProcessing}
                    showDropLine
                    onReorder={(reorder) => handleReorder(placement, anchor, fields, reorder)}
                    renderItem={(field, state) => (
                      <GlobalFieldRow
                        field={field}
                        dragHandleProps={state.dragHandleProps}
                        isDragDisabled={isFiltering || isProcessing}
                        isProcessing={isProcessing}
                        showPlacementColumn={showPlacementColumn}
                        subOrgIdToName={subOrgIdToName}
                        lobConfigurationsDict={lobConfigurationsDict}
                        subOrganizationsEnabled={!!organization.sub_organizations_enabled}
                        onEdit={(row) => setDialogState({ open: true, field: row })}
                        onDelete={handleDelete}
                        onEnabledChanged={handleEnabledChanged}
                      />
                    )}
                  />
                </div>
              ))}
              {/* The note ships with a filtered view too: a search is exactly when a user is most
                  likely to wonder why the handles went inert. */}
              {isFiltering ? (
                <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                  {REORDER_DISABLED_NOTE}
                </Text>
              ) : null}
            </div>
          )}
        </LoadingSwitch>
      </CardDialog>

      {dialogState.open && (
        <GlobalFieldDialog
          organizationId={organization.id}
          placements={placements}
          field={dialogState.field}
          onClose={() => setDialogState({ open: false, field: null })}
          onSaved={reloadData}
        />
      )}
    </>
  );
};

export default GlobalFieldsTab;
